import { Suspense, useEffect, useState } from 'react';
import type { NarrativeMiniValuatorScreen } from './utils';
import { NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM } from './utils';
import { NarrativeMiniValuatorCompanySelect } from './components/NarrativeMiniValuatorCompanySelectScreen';
import { NarrativeMiniValuatorValuationScreen } from './components/NarrativeMiniValuatorValuationScreen';
import { ErrorBoundary } from '@sentry/react';
import { Alert, AlertAction } from '@simplywallst/ui-core';
import { cn } from '@simplywallst/ui-core/next';
import { NarrativeMiniValuatorValuationSkeleton } from './components/NarrativeMiniValuatorValuationSkeleton';
import { useTracking } from 'react-tracking';
import { useQueryStringParam } from '@/hooks/useQueryStringParam';
import {
  getLastVisitedStockForMiniValuatorInThePastHour,
  removeLastVisitedStockForMiniValuator,
} from '../../utils/storage';
import type { HeadingElements } from '@/utilities/heading';
type Props = {
  companyCanonicalUrl?: string | null;
  className?: string;
  hideCompanySelect?: boolean;
  onSliderInteraction?: (isInteracting: boolean) => void;
  titleAs?: HeadingElements;
};

export const NarrativeMiniValuator = ({
  companyCanonicalUrl,
  className,
  hideCompanySelect,
  onSliderInteraction,
  titleAs = 'h2',
}: Props) => {
  const [screen, setScreen] =
    useState<NarrativeMiniValuatorScreen>('valuation');
  const { setItem, getItem, removeItem } = useQueryStringParam();
  const queryCanonicalUrl = getItem(
    NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM
  );
  const [canonicalUrl, setCanonicalUrl] = useState<string | null>(
    queryCanonicalUrl ?? companyCanonicalUrl ?? null
  );
  const handleCanonicalUrlChange = (canonicalUrl: string) => {
    if (canonicalUrl === companyCanonicalUrl) {
      removeItem(NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM);
    } else {
      setItem(NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM, canonicalUrl);
    }
    setCanonicalUrl(canonicalUrl);
  };

  useEffect(() => {
    if (companyCanonicalUrl) {
      handleCanonicalUrlChange(companyCanonicalUrl);
      return setScreen('valuation');
    }

    const lastVisitedCanonicalUrl =
      getLastVisitedStockForMiniValuatorInThePastHour();

    if (lastVisitedCanonicalUrl) {
      handleCanonicalUrlChange(lastVisitedCanonicalUrl);
      removeLastVisitedStockForMiniValuator();
      return setScreen('valuation');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCanonicalUrl]);

  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'narrative-mini-valuator',
  });

  const renderScreen = () => {
    if (!canonicalUrl || screen === 'companySelect')
      return (
        <NarrativeMiniValuatorCompanySelect
          onCompanySelect={(canonicalUrl) => {
            setScreen('valuation');
            handleCanonicalUrlChange(canonicalUrl);
            trackEvent({ action: 'company-selected', canonicalUrl });
          }}
          titleAs={titleAs}
        />
      );

    return (
      <NarrativeMiniValuatorValuationScreen
        key={canonicalUrl}
        canonicalUrl={canonicalUrl}
        hideCompanySelect={hideCompanySelect}
        setScreen={(newScreen) => {
          if (newScreen === 'companySelect')
            removeItem(NARRATIVE_MINI_VALUATOR_COMPANY_QUERY_PARAM);

          setScreen(newScreen);
        }}
        onSliderInteraction={onSliderInteraction}
        titleAs={titleAs}
      />
    );
  };
  return (
    <Track>
      <div
        className={cn(
          'scrollbar max-h-full overflow-y-auto bg-surface-1 text-solid @container',
          className
        )}
        role="complementary"
        aria-label="Narrative Mini Valuator"
      >
        <ErrorBoundary
          fallback={({ resetError }) => (
            <Alert
              styleType="negative"
              actions={
                <AlertAction
                  onClick={() => {
                    setScreen('companySelect');
                    resetError();
                  }}
                >
                  Select
                </AlertAction>
              }
            >
              Failed to load valuator for this company. Please select another
              one.
            </Alert>
          )}
        >
          <Suspense
            fallback={
              <NarrativeMiniValuatorValuationSkeleton
                hideCompanySelect={hideCompanySelect}
              />
            }
          >
            {renderScreen()}
          </Suspense>
        </ErrorBoundary>
      </div>
    </Track>
  );
};
